/**
 * AI CONTEXT
 * Purpose: the Don't share box on Standout Review, for a message or a photo.
 *   One optional free-text reason, the same shape as the old needs-work
 *   thumbs-down note ("Why does this need work? (optional)", 8584fa55,
 *   removed from the card in 9b622387). No checkboxes: no list of reasons
 *   exists in any history, so none are invented here.
 * Writes: none. The caller saves `dontShare` plus the trimmed reason and
 *   closes the box once that write succeeds.
 * Used by: StandoutMomentCard, ClassImageReviewList, MomentArtworkStrip.
 */
import { Input, Modal, Typography } from "antd"
import { useState } from "react"

const { Text } = Typography
const { TextArea } = Input

export type DontShareSubject = "message" | "photo"

const DontShareReasonModal = ({
  open,
  subject,
  pending = false,
  onConfirm,
  onCancel,
}: {
  open: boolean
  subject: DontShareSubject
  pending?: boolean
  onConfirm: (reason: string) => void
  onCancel: () => void
}) => {
  const [reason, setReason] = useState("")

  return (
    <Modal
      open={open}
      title={`Don't share this ${subject}?`}
      okText="Don't share"
      okButtonProps={{ danger: true, "data-testid": "sr-dont-share-confirm" }}
      cancelButtonProps={{
        "data-testid": "sr-dont-share-cancel",
        disabled: pending,
      }}
      confirmLoading={pending}
      onOk={() => {
        if (pending) return
        onConfirm(reason.trim())
      }}
      onCancel={onCancel}
      afterClose={() => setReason("")}
    >
      <div
        data-testid="sr-dont-share-box"
        style={{ display: "flex", flexDirection: "column", gap: 8 }}
      >
        <Text type="secondary">
          Parents will not get it. It stays in Jarvis, and Allow share turns it
          back on.
        </Text>
        <Text>Why not share it? (optional)</Text>
        <TextArea
          data-testid="sr-dont-share-reason-input"
          aria-label="Why not share it?"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </div>
    </Modal>
  )
}

/** The saved reason, shown while Don't share is on. Nothing when empty. */
export const DontShareReasonText = ({
  reason,
}: {
  reason: string | null | undefined
}) => {
  const text = reason?.trim()
  if (!text) return null
  return (
    <Text
      type="secondary"
      data-testid="sr-dont-share-reason"
      style={{ fontSize: 12, overflowWrap: "anywhere" }}
    >
      Not shared: {text}
    </Text>
  )
}

export default DontShareReasonModal
